// Per-country enriched Vision Health Report data.
// Aggregates (myopia, hyperopia, astigmatism, presbyopia, severity, correction)
// are sourced from REPORT.countries (report2026.ts). Per-country age + gender
// distributions are embedded directly below from the 2026 dataset.

import { REPORT, type CountryStats } from "./report2026";

export interface CountryAgeBucket {
  age_group: string;
  patients: number;
}

export interface CountryGender {
  female: number;
  male: number;
  other_unknown: number;
}

export interface CountryReport {
  slug: string;
  country: string;            // canonical English name used in REPORT
  /** % of screened patients with an identified need for glasses — null until supplied by the data team. */
  needs_correction: number | null;
  age_distribution: CountryAgeBucket[];
  gender: CountryGender;
}

// Map slug → country canonical name (aligned with src/config/countries.ts).
const COUNTRY_BY_SLUG: Record<string, string> = {
  "cote-divoire": "Cote d'Ivoire",
  togo: "Togo",
  "burkina-faso": "Burkina Faso",
  benin: "Benin",
  mali: "Mali",
  senegal: "Senegal",
  maroc: "Maroc",
  rdc: "DR Congo",
};

// Raw 2026 dataset — age distribution by country.
const AGE_BY_SLUG: Record<string, CountryAgeBucket[]> = {
  benin: [
    { age_group: "0-4", patients: 6041 },
    { age_group: "5-17", patients: 15753 },
    { age_group: "18-29", patients: 82004 },
    { age_group: "30-39", patients: 28125 },
    { age_group: "40-49", patients: 22716 },
    { age_group: "50-59", patients: 13387 },
    { age_group: "60+", patients: 9165 },
  ],
  "burkina-faso": [
    { age_group: "0-4", patients: 4049 },
    { age_group: "5-17", patients: 10434 },
    { age_group: "18-29", patients: 61559 },
    { age_group: "30-39", patients: 40235 },
    { age_group: "40-49", patients: 37566 },
    { age_group: "50-59", patients: 21351 },
    { age_group: "60+", patients: 11893 },
  ],
  rdc: [
    { age_group: "0-4", patients: 577 },
    { age_group: "5-17", patients: 8 },
    { age_group: "18-29", patients: 134 },
    { age_group: "30-39", patients: 113 },
    { age_group: "40-49", patients: 106 },
    { age_group: "50-59", patients: 74 },
    { age_group: "60+", patients: 35 },
  ],
  "cote-divoire": [
    { age_group: "0-4", patients: 4860 },
    { age_group: "5-17", patients: 32117 },
    { age_group: "18-29", patients: 133329 },
    { age_group: "30-39", patients: 65131 },
    { age_group: "40-49", patients: 54605 },
    { age_group: "50-59", patients: 29976 },
    { age_group: "60+", patients: 15727 },
  ],
  mali: [
    { age_group: "0-4", patients: 4785 },
    { age_group: "5-17", patients: 10989 },
    { age_group: "18-29", patients: 57428 },
    { age_group: "30-39", patients: 32786 },
    { age_group: "40-49", patients: 24278 },
    { age_group: "50-59", patients: 13763 },
    { age_group: "60+", patients: 9476 },
  ],
  maroc: [
    { age_group: "0-4", patients: 4819 },
    { age_group: "5-17", patients: 310 },
    { age_group: "18-29", patients: 2116 },
    { age_group: "30-39", patients: 1623 },
    { age_group: "40-49", patients: 1207 },
    { age_group: "50-59", patients: 887 },
    { age_group: "60+", patients: 723 },
  ],
  senegal: [
    { age_group: "0-4", patients: 248 },
    { age_group: "5-17", patients: 2595 },
    { age_group: "18-29", patients: 7536 },
    { age_group: "30-39", patients: 4866 },
    { age_group: "40-49", patients: 5708 },
    { age_group: "50-59", patients: 4639 },
    { age_group: "60+", patients: 4218 },
  ],
  togo: [
    { age_group: "0-4", patients: 5832 },
    { age_group: "5-17", patients: 19619 },
    { age_group: "18-29", patients: 116430 },
    { age_group: "30-39", patients: 42790 },
    { age_group: "40-49", patients: 33513 },
    { age_group: "50-59", patients: 19915 },
    { age_group: "60+", patients: 13804 },
  ],
};

// Raw 2026 dataset — gender counts by country.
const GENDER_BY_SLUG: Record<string, CountryGender> = {
  benin: { female: 75868, male: 95874, other_unknown: 162 },
  "burkina-faso": { female: 83889, male: 100580, other_unknown: 193 },
  rdc: { female: 343, male: 706, other_unknown: 0 },
  "cote-divoire": { female: 170996, male: 161603, other_unknown: 294 },
  mali: { female: 55740, male: 96442, other_unknown: 55 },
  maroc: { female: 7030, male: 4653, other_unknown: 16 },
  senegal: { female: 14864, male: 14944, other_unknown: 10 },
  togo: { female: 126229, male: 123920, other_unknown: 189 },
};

export const COUNTRY_REPORTS: Record<string, CountryReport> = Object.fromEntries(
  Object.entries(COUNTRY_BY_SLUG).map(([slug, country]) => {
    const stats = REPORT.countries.find((c) => c.country === country);
    if (!stats) throw new Error(`No REPORT.countries entry for ${country}`);
    return [
      slug,
      {
        slug,
        country,
        needs_correction:
          stats.needs_correction_n != null && stats.patients > 0
            ? (stats.needs_correction_n / stats.patients) * 100
            : null,
        age_distribution: AGE_BY_SLUG[slug] ?? [],
        gender: GENDER_BY_SLUG[slug] ?? { female: 0, male: 0, other_unknown: 0 },
      } satisfies CountryReport,
    ];
  })
);

export function getCountryReport(slug: string | undefined): CountryReport | null {
  if (!slug) return null;
  return COUNTRY_REPORTS[slug] ?? null;
}

export function getCountryStats(slug: string): CountryStats | null {
  const r = COUNTRY_REPORTS[slug];
  if (!r) return null;
  return REPORT.countries.find((c) => c.country === r.country) ?? null;
}

// Africa-wide weighted averages computed once from REPORT.countries.
export const AFRICA_AVG = (() => {
  const totalPatients = REPORT.countries.reduce((s, c) => s + c.patients, 0);
  const wAvg = (key: keyof Pick<CountryStats, "myopia" | "hyperopia" | "astigmatism" | "presbyopia" | "severity_score">) =>
    REPORT.countries.reduce((s, c) => s + (c[key] as number) * c.patients, 0) / totalPatients;
  return {
    patients: totalPatients,
    myopia: wAvg("myopia"),
    hyperopia: wAvg("hyperopia"),
    astigmatism: wAvg("astigmatism"),
    presbyopia: wAvg("presbyopia"),
    severity_score: wAvg("severity_score"),
  };
})();

export function rankBy(
  slug: string,
  key: "myopia" | "hyperopia" | "astigmatism" | "presbyopia" | "severity_score",
  order: "asc" | "desc" = "desc"
): { rank: number; total: number } | null {
  const r = COUNTRY_REPORTS[slug];
  if (!r) return null;
  const sorted = [...REPORT.countries].sort((a, b) =>
    order === "desc" ? (b[key] as number) - (a[key] as number) : (a[key] as number) - (b[key] as number)
  );
  const idx = sorted.findIndex((c) => c.country === r.country);
  return { rank: idx + 1, total: sorted.length };
}

/** Rang du pays selon la part des personnes testées avec un besoin de lunettes identifié (indicateur santé uniquement).
 * Rang 1 = part de besoin la plus faible (meilleure santé visuelle) ; pays sans effectif validé classés en dernier. */
export function visionHealthRank(slug: string): { rank: number; total: number } | null {
  const r = COUNTRY_REPORTS[slug];
  if (!r) return null;
  const scored = REPORT.countries.map((c) => ({
    country: c.country,
    score: c.needs_correction_n != null ? c.needs_correction_n / c.patients : -1,
  }));
  scored.sort((a, b) => a.score - b.score);
  const idx = scored.findIndex((c) => c.country === r.country);
  return { rank: idx + 1, total: scored.length };
}
